import React, { useEffect } from 'react';
import { useObserver } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useQuery } from '@apollo/client';

import { ModalWrapper } from 'shared/modals/v2-wrapper';
import { DesktopOnly, MobileOnly } from 'shared/components';
import { ERNIE_TYPES } from 'shared/constants';
import useErnie from 'shared/hooks/use-ernie';

import useUser from 'src/hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { GetDutchiePosLoyaltyConfigDocument } from 'src/gql/graphql';
import { ModalContainer, BottomDrawer } from 'src/components/core';
import { withModalControlsHook, ModalComponentProps } from 'src/components/modals/with-modal-controls-hook';
import { Loyalty } from 'src/components/loyalty/modal';
import {
  detectLoyaltyIntegration,
  Integrations,
  Statuses,
  getEnrollmentRequiresPhoneVerification,
  verificationRequiredPrompts,
} from 'src/components/loyalty/use-loyalty';
import type { LoyaltyEnrollmentSurface } from 'src/utils/analytics/events';

export type LoyaltyModalDataValue = {
  surface: LoyaltyEnrollmentSurface;
};

type LoyaltyModalProps = ModalComponentProps<LoyaltyModalDataValue, { status: Statuses } | undefined>;

const LoyaltyModal = ({ onClose, containerProps, data }: LoyaltyModalProps): JSX.Element | null => {
  const flags = useFlags();
  const showErnie = useErnie();
  const User = useUser();
  const { dispensary } = useDispensary();
  const loyaltyProgram = detectLoyaltyIntegration(dispensary);
  const isDutchiePos = loyaltyProgram === Integrations.dutchiepos;
  const isEnrollmentGateEnabled = flags['pos.ecomm.loyalty-enrollment-verified-phone.rollout'] ?? false;
  const phoneVerifiedAt = useObserver(() => User.phoneVerifiedAt);
  const phone = useObserver(() => User.phone);

  const { data: configData, loading: configLoading } = useQuery(GetDutchiePosLoyaltyConfigDocument, {
    variables: { dispensaryId: dispensary?.id },
    skip: !isEnrollmentGateEnabled || !isDutchiePos || !dispensary?.id,
  });

  const requiresPhoneVerification =
    isDutchiePos &&
    getEnrollmentRequiresPhoneVerification({
      loyaltyConfig: configData?.getDutchiePosLoyaltyConfig,
      phoneVerifiedAt,
      isEnrollmentPhoneVerificationGateEnabled: isEnrollmentGateEnabled,
    });

  useEffect(() => {
    if (!configLoading && requiresPhoneVerification) {
      const prompt = !phone ? verificationRequiredPrompts.phone_missing : verificationRequiredPrompts.phone_unverified;
      showErnie(prompt, ERNIE_TYPES.DANGER);
      onClose({ status: Statuses.verificationRequired });
    }
  }, [configLoading, requiresPhoneVerification, phone, showErnie, onClose]);

  if (isEnrollmentGateEnabled && isDutchiePos && configLoading) {
    return null;
  }

  if (requiresPhoneVerification) {
    return null;
  }

  return (
    <>
      <DesktopOnly mediaQuery='sm'>
        <ModalWrapper open>
          <ModalContainer width='470px' p='32px' {...containerProps}>
            <Loyalty onClose={onClose} surface={data.surface} />
          </ModalContainer>
        </ModalWrapper>
      </DesktopOnly>
      <MobileOnly mediaQuery='xs'>
        <BottomDrawer open hideExitIcon onClose={onClose} shouldCalculateIframeBottom {...containerProps}>
          <Loyalty onClose={onClose} surface={data.surface} />
        </BottomDrawer>
      </MobileOnly>
    </>
  );
};

const [LoyaltyController, useLoyaltyModal] = withModalControlsHook<
  LoyaltyModalDataValue,
  { status: Statuses } | undefined
>(LoyaltyModal);

export { useLoyaltyModal, LoyaltyController, LoyaltyModal };
